import React, { useState, useMemo, useEffect, useContext } from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Avatar
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import BadgeIcon from '@mui/icons-material/Badge';
import PersonIcon from '@mui/icons-material/Person';
import WorkIcon from '@mui/icons-material/Work';
import CustomCard from '../../components/UIComponents/CustomCard';
import BasicTable from '../../components/Table';
import { useNavigate } from 'react-router-dom';
import { Employeedetails } from '../../service/network';
import { useLoading } from "../../context/loading.context";
import StyledAutocomplete from '../../components/UIComponents/StyledAutocomplete';
import StyledTextField from '../../components/UIComponents/StyledTextField';
import { AuthContext } from '../../context/auth.context';

const StatusBadge = ({ status }) => {
  const normalizedStatus = status
    ? status.trim().charAt(0).toUpperCase() + status.trim().slice(1).toLowerCase()
    : 'Unknown';

  const statusConfig = {
    'Active': {
      icon: <CheckCircleIcon fontSize="small" />,
      bgColor: '#e8f5e9',
      textColor: '#4caf50'
    },
    'Inactive': {
      icon: <CancelIcon fontSize="small" />,
      bgColor: '#ffebee',
      textColor: '#f44336'
    },
    'Suspended': {
      icon: <PauseCircleOutlineIcon fontSize="small" />,
      bgColor: '#fff8e1',
      textColor: '#ff9800'
    },
    'Left': {
      icon: <ExitToAppIcon fontSize="small" />,
      bgColor: '#f5f5f5',
      textColor: '#9e9e9e'
    },
    'On leave': {
      icon: <BeachAccessIcon fontSize="small" />,
      bgColor: '#e3f2fd',
      textColor: '#2196f3'
    }
  };

  const config = statusConfig[normalizedStatus] || {
    icon: <HelpOutlineIcon fontSize="small" />,
    bgColor: '#f5f5f5',
    textColor: '#9e9e9e'
  };

  return (
    <Chip
      icon={config.icon}
      label={normalizedStatus}
      sx={{
        backgroundColor: config.bgColor,
        color: config.textColor,
        fontWeight: 600,
        minWidth: '100px',
        '.MuiChip-icon': {
          color: config.textColor,
          marginLeft: '4px'
        }
      }}
      size="small"
    />
  );
};

export default function Employee() {
  const [employees, setEmployees] = useState([]);

  const [filters, setFilters] = useState({
    employeeId: '',
    employeeName: '',
    department: '',
    company: '',
    branch: ''
  });
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [branchOptions, setBranchOptions] = useState([]);

  const navigate = useNavigate();
  const { showLoader, hideLoader } = useLoading();
  const { selLocation } = useContext(AuthContext);

  const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);

      return () => {
        clearTimeout(handler);
      };
    }, [value, delay]);

    return debouncedValue;
  };

  useEffect(() => {
    if (selLocation && selLocation[0]) {
      setFilters(prev => ({
        ...prev,
        company: selLocation[0]
      }));
    }
  }, [selLocation]);

  // Debounce the filters
  const debouncedEmployeeId = useDebounce(filters.employeeId, 500);
  const debouncedEmployeeName = useDebounce(filters.employeeName, 500);
  const debouncedDepartment = useDebounce(filters.department, 500);
  const debouncedBranch = useDebounce(filters.branch, 500);

  const employeeTableConfig = {
    rows: [
      {
        id: 'employee_id',
        label: 'Employee ID',
        flex: 1,
        minWidth: '120px',
        accessor: 'employee_id',
        align: 'left',
        canSort: false
      },
      {
        id: 'employee_name',
        label: 'Name',
        flex: 1.5,
        minWidth: '180px',
        accessor: 'employee_name',
        align: 'left',
        canSort: false,
        render: (row) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {row.employee_name}
          </Box>
        )
      },
         {
        id: 'gender',
        label: 'Gender',
        flex: 1,
        minWidth: '120px',
        accessor: 'gender',
        align: 'left',
        canSort: false
      },
      {
        id: 'designation',
        label: 'Designation',
        flex: 1.2,
        minWidth: '150px',
        accessor: 'designation',
        align: 'left',
        canSort: false
      },
      {
        id: 'department',
        label: 'Department',
        flex: 1.2,
        minWidth: '150px',
        accessor: 'department',
        align: 'left',
        canSort: false
      },
      {
        id: 'branch',
        label: 'Branch',
        flex: 1.2,
        minWidth: '150px',
        accessor: 'branch',
        align: 'left',
        canSort: false
      },
   
      {
        id: 'ctc',
        label: 'CTC',
        flex: 1,
        minWidth: '120px',
        accessor: 'ctc',
        align: 'center',
        canSort: false,
        render: (row) => row.ctc ? `₹${row.ctc}` : 'N/A'
      },
      {
        id: 'status',
        label: 'Status',
        flex: 1,
        minWidth: '120px',
        accessor: 'status',
        align: 'center',
        canSort: false,
        render: (row) => <StatusBadge status={row.status} />
      },
    ]
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddEmployee = () => {
    navigate('/employee/add');
  };

  const handleRowClick = (rowData) => {
    navigate('/employee/add', {
      state: {
        editMode: true,
        employeeId: rowData.employee_id
      }
    });
  };

  const buildApiFilters = () => {
    const apiFilters = [];

    // Add company filter if set
    if (filters.company) {
      apiFilters.push(['company', '=', filters.company]);
    }

    // Add employee ID filter if set
    if (filters.employeeId) {
      apiFilters.push(['name', 'like', `%${filters.employeeId}%`]);
    }

    // Add employee name filter if set
    if (filters.employeeName) {
      apiFilters.push(['employee_name', 'like', `%${filters.employeeName}%`]);
    }

    // Add department filter if set
    if (filters.department) {
      apiFilters.push(['department', 'descendants of (inclusive)', filters.department]);
    }

    // Add branch filter if set
    if (filters.branch) {
      apiFilters.push(['branch', '=', filters.branch]);
    }
    return apiFilters;
  };

  const fetchEmployees = async () => {
    try {
      showLoader(true);
      const queryParams = new URLSearchParams({
        doctype: 'Employee',
        fields: JSON.stringify([
          "name as employee_id",
          "employee_name",
          "status",
          "designation",
          "department",
          "branch",
          "gender",
          "ctc",
          "company"
        ]),
        limit_start: '0',
        limit_page_length: '2000',
        order_by: 'modified desc'
      });

      const apiFilters = buildApiFilters();
      if (apiFilters.length > 0) {
        queryParams.append('filters', JSON.stringify(apiFilters));
      }

      const response = await Employeedetails(queryParams.toString());

      if (!response?.data?.message) {
        throw new Error('No employee data found');
      }

      const employeeData = response.data.message;

      // If the API returns an array of arrays (values), transform it
      let transformedData;
      if (Array.isArray(employeeData.values)) {
        transformedData = employeeData.values.map(emp => ({
          employee_id: emp[0] || '',
          employee_name: emp[1] || 'Unknown',
          status: emp[2] || 'Inactive',
          designation: emp[3] || 'N/A',
          department: emp[4] || 'N/A',
          branch: emp[5] || 'N/A',
          gender: emp[6] || 'N/A',
          ctc: emp[7] || '',
          company: emp[8] || ''
        }));
      } else if (Array.isArray(employeeData)) {
        // If the API returns an array of objects directly
        transformedData = employeeData.map(emp => ({
          employee_id: emp.employee_id || emp.name || '',
          employee_name: emp.employee_name || 'Unknown',
          status: emp.status || 'Inactive',
          designation: emp.designation || 'N/A',
          department: emp.department || 'N/A',
          branch: emp.branch || 'N/A',
          gender: emp.gender || 'N/A',
          ctc: emp.ctc || '',
          company: emp.company || ''
        }));
      } else {
        throw new Error('Unexpected data format from API');
      }

      setEmployees(transformedData);
    } catch (error) {
      console.error('Fetch error:', error);
      setEmployees([]);
    } finally {
      hideLoader();
    }
  };

  const fetchOptions = async (doctype, setterFunction) => {
    try {
      const queryParams = new URLSearchParams({
        doctype,
        fields: JSON.stringify(['name'])
      });

      const response = await Employeedetails(queryParams.toString());

      if (!response?.data?.message?.values) {
        throw new Error(`No data found for ${doctype}`);
      }

      const options = response.data.message.values.map(v => v[0]);
      setterFunction(options);
    } catch (error) {
      console.error(`Failed to fetch ${doctype} options:`, error);
      setterFunction([]);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, [debouncedEmployeeId, debouncedEmployeeName, debouncedDepartment, debouncedBranch, filters.company]);

  useEffect(() => {
    fetchOptions('Department', setDepartmentOptions);
    fetchOptions('Branch', setBranchOptions);
  }, []);

  return (
    <Box sx={{ width: '100%', p: { xs: 1, sm: 2 }, boxSizing: 'border-box' }}>
      <CustomCard
        bgColor="light"
        shadow="lg"
        borderRadius="xxl"
        sx={{
          width: '100%',
          minHeight: 800,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          elevation: 2,
          p: 3
        }}
      >
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          flexWrap: 'wrap',
          gap: 2
        }}>
          <Box>
            <Typography variant="subTitle" color='primary.main' >
              Employee List
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Overview of Employee Details
            </Typography>
          </Box>

          <Button
            variant="contained"
            color="primary"
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              px: 3,
              height: '40px'
            }}
            onClick={handleAddEmployee}
          >
            Add Employee
          </Button>
        </Box>

        {/* Filter Section */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Grid size={2}>
            <StyledTextField
              label="Employee ID"
              value={filters.employeeId}
              onChange={(e) => handleFilterChange('employeeId', e.target.value)}
              fullWidth
              placeholder="Search ID"
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BadgeIcon color="action" />
                  </InputAdornment>
                )
              }}
            />
          </Grid>

          <Grid size={2}>
            <StyledTextField
              label="Employee Name"
              value={filters.employeeName}
              onChange={(e) => handleFilterChange('employeeName', e.target.value)}
              fullWidth
              size="small"
              placeholder="Search Name"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon color="action" />
                  </InputAdornment>
                )
              }}
            />
          </Grid>

          <Grid size={2}>
            <StyledAutocomplete
              label="Department"
              options={departmentOptions}
              value={filters.department}
              onChange={(e, newValue) => handleFilterChange('department', newValue)}
              fullWidth
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <WorkIcon color="action" />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid size={2}>
            <StyledAutocomplete
              label="Branch"
              options={branchOptions}
              value={filters.branch}
              onChange={(e, newValue) => handleFilterChange('branch', newValue)}
              fullWidth
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <WorkIcon color="action" />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid size={2}>
            <StyledTextField
              label="Company"
              value={filters.company}
              onChange={(e) => handleFilterChange('company', e.target.value)}
              fullWidth
              size="small"
              disabled
            />
          </Grid>
        </Box>

        <Box sx={{ flex: 1 }}>
          <BasicTable
            data={{
              ...employeeTableConfig,
              columns: employees
            }}
            onRowClick={handleRowClick}
            backgroundColor="#f9fafc"
          />
        </Box>
      </CustomCard>
    </Box>
  );
}